import cc from "classcat"
import { Feature, useFeature } from "flagged"
import React, { useEffect, useMemo } from "react"
import { Control, Controller, UseFormGetValues } from "react-hook-form"
import { graphql, useLazyLoadQuery } from "react-relay"
import { components, OptionProps } from "react-select"

import styles from "./Panel.module.css"

import { ProjectsPanelQuery } from "./__generated__/ProjectsPanelQuery.graphql"

import Select from "~/common/Select"
import Callout from "~/common/Text/Callout"
import Support from "~/common/Text/SupportingText"
import Checkbox from "~/common/inputs/Checkbox"

import { UserForm } from "../userForm"
import { keyBy } from "lodash-es"
import { Tooltip } from "@blueprintjs/core"

const PROJECT_ACCESS_OPTIONS = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Restricted",
    value: "restricted",
  },
]

const QUERY = graphql`
  query ProjectsPanelQuery {
    current_user {
      id
      account {
        id
        projects(
          where: { is_template: { _eq: false }, active: { _eq: true } }
        ) {
          id
          name
          team_id
        }
        teams {
          id
          name
          projects {
            id
            name
          }
        }
      }
    }
  }
`

const ProjectAccessLabel = (
  <div className={cc([styles.label, styles.header])}>Project Access</div>
)

const RestrictedProjectsSelectLabel = (
  <div className={cc([styles.label, styles.header])}>
    Select restricted projects by
  </div>
)

const ManagedProjectsLabel = (
  <div className={styles.label}>
    Managed Projects
    <span className={styles.sublabel}>
      Manually select specific people to grant access.
    </span>
  </div>
)

const ManagedPrimaryTeamsLabel = (
  <div className={styles.label}>
    Primary Teams
    <span className={styles.sublabel}>
      User can edit any projects that have the primary team(s) assigned.
    </span>
  </div>
)

type ProjectOptionProps = {
  label: string
  value: string
  isDisabled?: boolean
  disabledReason?: string
}

const ProjectOption = (props: OptionProps<ProjectOptionProps>) => {
  return (
    <components.Option {...props} isDisabled={props.data.isDisabled}>
      <div
        className={styles.projectOption}
        aria-disabled={props.data.isDisabled}
      >
        <span>{props.data.label}</span>
        {props.data.disabledReason && (
          <span> {props.data.disabledReason} </span>
        )}
      </div>
    </components.Option>
  )
}

type Props = {
  control: Control<UserForm>
  getValues: UseFormGetValues<UserForm>
  setProjectsCount: (count: number) => void
}

const ProjectsPanel = ({ control, getValues, setProjectsCount }: Props) => {
  const data = useLazyLoadQuery<ProjectsPanelQuery>(QUERY, {})
  const { projects, teams } = data.current_user.account

  const isProjectTeamPermissionEnabled = useFeature("project_team_permissions")

  const manageProjectsPermission = getValues("permissions.manage_projects")
  const managePeoplePermission = getValues("permissions.manage_people")
  const manageableProjectTeams = getValues("manageableProjectTeams")
  const manageableProjects = getValues("manageableProjects")

  const teamsById = useMemo(() => keyBy(teams, "id"), [teams])

  const manageableTeamProjects = useMemo(
    () =>
      projects?.filter(
        (project) =>
          project.team_id && manageableProjectTeams.includes(project.team_id),
      ) || [],
    [projects, manageableProjectTeams],
  )

  useEffect(() => {
    setProjectsCount(manageableTeamProjects.length + manageableProjects.length)
  }, [
    manageableTeamProjects.length,
    manageableProjects.length,
    setProjectsCount,
  ])

  const projectOptions = useMemo(
    () =>
      projects?.map((project) => {
        const teamManagingProject =
          project.team_id &&
          manageableProjectTeams.find((teamId) => teamId === project.team_id)
        const teamManagingProjectName = teamsById[teamManagingProject]?.name

        return {
          value: project.id,
          label: project.name,
          isDisabled: Boolean(teamManagingProject),
          disabledReason: teamManagingProjectName
            ? `Added via ${teamManagingProjectName}`
            : undefined,
        }
      }) ?? [],
    [projects, teamsById, manageableProjectTeams],
  )
  const teamOptions = useMemo(
    () =>
      teams?.map((team) => ({
        value: team.id,
        label: team.name,
      })) ?? [],
    [teams],
  )

  return (
    <div className={styles.panel} style={{ gap: "32px" }}>
      <div>
        <Controller
          control={control}
          name="permissions.manage_projects"
          render={({ field: { onChange, value } }) => (
            <Select
              className={cc([
                styles.input,
                {
                  [styles.half]: isProjectTeamPermissionEnabled,
                },
              ])}
              onChange={(e) => onChange(e.value)}
              label={ProjectAccessLabel}
              value={PROJECT_ACCESS_OPTIONS.find(
                (option) => option.value === value,
              )}
              options={PROJECT_ACCESS_OPTIONS}
            />
          )}
        />
        <Support>
          {getValues("permissions.manage_projects") === "all"
            ? "Can edit All Projects"
            : "Can edit selected Projects"}
        </Support>
      </div>
      {manageProjectsPermission === "restricted" && (
        <div>
          {RestrictedProjectsSelectLabel}
          <div className={styles.permissions}>
            <Feature name="project_team_permissions">
              <div className={cc([styles.section, styles.border])}>
                <Controller
                  control={control}
                  name="manageableProjectTeams"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      id="managed-project-teams"
                      value={teamOptions.filter((option) =>
                        value.includes(option.value),
                      )}
                      isMulti
                      onChange={(e) => onChange(e.map((item) => item.value))}
                      label={ManagedPrimaryTeamsLabel}
                      options={teamOptions}
                      placeholder="Type Team name..."
                      dataTest="manage-teams-select"
                    />
                  )}
                />
              </div>
            </Feature>
            <div className={cc([styles.section, styles.border])}>
              <>
                <Controller
                  control={control}
                  name="manageableProjects"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      id="managed-projects"
                      value={projectOptions.filter((option) =>
                        value.includes(option.value),
                      )}
                      isMulti
                      onChange={(e) => onChange(e.map((item) => item.value))}
                      label={ManagedProjectsLabel}
                      options={projectOptions}
                      placeholder="Type Project name..."
                      components={{ Option: ProjectOption }}
                    />
                  )}
                />
                {manageableTeamProjects.length > 0 && (
                  <Tooltip
                    hoverOpenDelay={300}
                    placement="top-start"
                    minimal
                    content={
                      <div className={styles.projectTooltip}>
                        {manageableTeamProjects.slice(0, 20).map((project) => (
                          <div key={project.id} className={styles.tag}>
                            {project.name}
                          </div>
                        ))}
                        {manageableTeamProjects.length > 20 && (
                          <span className={styles.moreProjects}>
                            +{manageableTeamProjects.length - 20} more...
                          </span>
                        )}
                      </div>
                    }
                    matchTargetWidth
                  >
                    <div className={styles.sublabel}>
                      <span className={styles.teamProjectsSublabel}>
                        {manageableTeamProjects.length} Projects
                      </span>{" "}
                      added via Primary Team
                    </div>
                  </Tooltip>
                )}
              </>
            </div>
          </div>
        </div>
      )}
      {managePeoplePermission !== "all" && (
        <div>
          <div className={styles.section}>
            <Controller
              control={control}
              name="permissions.add_all_people_to_projects"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  id="add_all_people_to_projects"
                  label="Can assign all People from the account to Managed Projects"
                  labelClassName={styles.checkboxLabel}
                  onChange={onChange}
                  checked={value}
                />
              )}
            />
          </div>
          {managePeoplePermission === "restricted" && (
            <Callout className={styles.attachedCallout}>
              {"Users can add their Managed People to projects by default. "}
            </Callout>
          )}
        </div>
      )}
    </div>
  )
}

export default ProjectsPanel
